import React, {useState, useCallback, useEffect} from 'react';
import {Box, Stack} from '@mui/material';
import {Headline, Button} from '@lightricks/react-design-system';
import Textarea from '@/components/textarea';
import StarRating from './StarRating';

export interface AnalysisRatingFormProps {
  initialRating: number | null;
  initialNote: string | null;
  onSubmit: (payload: {
    analysisRating: number | null;
    analysisNote: string | null;
  }) => void;
  isSubmitting: boolean;
}

function AnalysisRatingForm({
  initialRating,
  initialNote,
  onSubmit,
  isSubmitting,
}: AnalysisRatingFormProps) {
  const [rating, setRating] = useState<number | null>(initialRating ?? null);
  const [note, setNote] = useState(initialNote ?? '');

  useEffect(() => {
    setRating(initialRating ?? null);
    setNote(initialNote ?? '');
  }, [initialRating, initialNote]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit({
        analysisRating: rating,
        analysisNote: note.trim() || null,
      });
    },
    [onSubmit, rating, note]
  );

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        backgroundColor: '#f5f5f5',
        padding: 2,
        marginBottom: 3,
      }}
    >
      <Stack spacing={2}>
        <Headline size="sm">Analysis rating</Headline>

        <StarRating value={rating} onChange={setRating} />

        <Textarea
          label="Analysis notes"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What helped? What didn’t? Your feedback shapes future analyses."
          minRows={5}
          testID="analysis-rating-note"
        />

        <Box sx={{alignSelf: 'flex-start'}}>
          <Button
            type="submit"
            disabled={isSubmitting}
            appearance="neutral"
            mode="filled"
            size="medium"
          >
            {isSubmitting ? 'Saving…' : 'Save rating & note'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export default AnalysisRatingForm;
